//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  IDefaultInstagramEngagersRequest,
  IDefaultInstagramEngagersResponse,
  IDefaultInstagramUserMediaRequest,
  IDefaultInstagramUserPostsResponse,
  IDefaultInstagramUserResponse,
  IExportInstagramEngagementPostPerformanceRequestBody,
  IInstagramDefaultUserLivePostsRequest,
  IInstagramDefaultUserPostsLabelsAutocompleteRequest,
  IInstagramDefaultUserPostsLabelsAutocompleteResponse,
  IInstagramDefaultUserPostsLabelsRequest,
  IInstagramDefaultUserPostsLabelsResponse,
  IInstagramDefaultUserPostsStatsRequest,
  IInstagramDefaultUserPostsStatsResponse,
} from '@apis/instagram-user/instagramUserApiModel';
import { ILabel } from '@models/ILabel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';

const INSTAGRAM_USER_DEFAULT_ENDPOINT = '/api/v2/instagram/user/default';

const instagramDefaultUserFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<Response> => {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `${INSTAGRAM_USER_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * The CSV file export url for posts for a client's default Instagram account from NDW
 *
 * /api/v2/instagram/user/default/posts.csv
 */

// TODO: (PD-187157) Delete after PD-187157 is completed
export const INSTAGRAM_USER_POSTS_EXPORT_URL = `${CURALATE_ENV.INSTAGRAM_DASHBOARD_GATEWAY_URL}${INSTAGRAM_USER_DEFAULT_ENDPOINT}/posts.csv`;

/**
 * Gets mined data for the current client's connected Instagram account.
 *
 * /api/v2/instagram/user/default
 */
export const getDefaultInstagramUser = async (
  init?: RequestInit,
): Promise<IDefaultInstagramUserResponse> => {
  const res = await instagramDefaultUserFetch('', init);
  return res.json();
};

/**
 * WARNING: Only enabled for client 14503 (At Home) with feature flag InstagramEngagementCsvDirectDownload
 * DO NOT USE ELSEWHERE!
 *
 * Returns the CSV file export for the current client's default Instagram account
 * for dates and post type specified in the `params`.
 *
 * Returns a maximum of 100 posts.
 *
 * /api/v2/instagram/user/default/posts.csv?
 */
export async function getInstagramEngagementPostsCsv(
  req: IDefaultInstagramUserMediaRequest,
): Promise<string> {
  const params = makeParams(req);
  const res = await instagramDefaultUserFetch(`/posts.csv?${params}`);

  return await res.text();
}

/**
 * Requests the creation of the post performance CSV file export containing
 * ALL posts for dates and post type specified in the `params`, which is
 * uploaded to s3 and triggers an email to the user with a link to download the export.
 *
 * POST /api/v2/instagram/user/default/posts/performance-exports
 *
 */
export async function getExportInstagramEngagementPostPerformanceToEmail(
  body: IExportInstagramEngagementPostPerformanceRequestBody,
): Promise<void> {
  await instagramDefaultUserFetch(`/posts/post-performance-exports`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
}

/**
 * Returns the above post performance export via the link that was emailed to the client
 * for the current client's default Instagram account.
 *
 * GET /api/v2/instagram/user/default/posts/performance-exports
 *
 */
export async function getInstagramEngagementPostPerformanceExport(
  exportId: string,
): Promise<Response> {
  return await instagramDefaultUserFetch(
    `/posts/post-performance-exports/${exportId}`,
  );
}

/**
 * Gets live data for the current client's connected Instagram account.
 *
 * /api/v2/instagram/user/default/live
 */
export const getLiveDefaultInstagramUser =
  async (): Promise<IDefaultInstagramUserResponse> => {
    const res = await instagramDefaultUserFetch('/live');
    return res.json();
  };

/**
 * Gets NDW posts associated with the current client's connected Instagram account,
 * per the given request parameters.
 *
 * /api/v2/instagram/user/default/posts
 */
export const getDefaultInstagramUserPosts = async (
  req?: IDefaultInstagramUserMediaRequest,
  init?: RequestInit,
): Promise<IDefaultInstagramUserPostsResponse> => {
  const params = makeParams(req);
  const res = await instagramDefaultUserFetch(`/posts?${params}`, init);
  return res.json();
};

/**
 * Gets the latest Instagram posts for the current client's connected Instagram account.
 *
 * This hits the live graph API rather than USS, so use
 * this sparingly to protect our rate limits!
 *
 * /api/v2/instagram/user/default/posts/live
 */
export const getDefaultInstagramUserLivePosts = async (
  req?: IInstagramDefaultUserLivePostsRequest,
): Promise<IDefaultInstagramUserPostsResponse> => {
  const params = $.param(req ?? {});
  const res = await instagramDefaultUserFetch(`/posts/live?${params}`);
  return res.json();
};

/**
 * Gets trend and aggregate metrics for posts associated with the current client's connected Instagram
 * account, per the given request parameters.
 *
 * /api/v2/instagram/user/default/posts/stats
 */
export const getDefaultInstagramUserPostsStats = async (
  req?: IInstagramDefaultUserPostsStatsRequest,
  init?: RequestInit,
): Promise<IInstagramDefaultUserPostsStatsResponse> => {
  const params = makeParams(req);
  const res = await instagramDefaultUserFetch(`/posts/stats?${params}`, init);
  return res.json();
};

/**
 * Gets labels associated with posts for the current client's connected Instagram
 * account, per the given request parameters.
 *
 * /api/v2/instagram/user/default/posts/labels
 */
export const getDefaultInstagramUserPostsLabels = async (
  req?: IInstagramDefaultUserPostsLabelsRequest,
): Promise<ILabel[]> => {
  const params = makeParams(req);
  const res = await instagramDefaultUserFetch(`/posts/labels?${params}`);

  const labelsResponse =
    (await res.json()) as IInstagramDefaultUserPostsLabelsResponse;

  return Object.entries(labelsResponse.data.labels).map(([label, count]) => {
    return {
      label,
      count,
      value: label,
    };
  });
};

/**
 * Autocompletes labels associated with posts for the current client's connected Instagram
 * account, per the given request parameters.
 *
 * /api/v2/instagram/user/default/posts/labels/autocomplete
 */
export const getDefaultInstagramUserPostsLabelsAutocomplete = async (
  req?: IInstagramDefaultUserPostsLabelsAutocompleteRequest,
): Promise<ILabel[]> => {
  const params = makeParams(req);
  const res = await instagramDefaultUserFetch(
    `/posts/labels/autocomplete?${params}`,
  );

  const autocompleteResponse =
    (await res.json()) as IInstagramDefaultUserPostsLabelsAutocompleteResponse;

  return autocompleteResponse.data.labels.map(([value, count]) => {
    return {
      label: value,
      count,
      value,
    };
  });
};

/*
 * Gets the top recent engagers for the current client's connected Instagram account,
 * per the given request parameters.
 *
 * /api/v2/instagram/user/default/engagers
 */
export const getDefaultInstagramTopRecentEngagers = async (
  req?: IDefaultInstagramEngagersRequest,
): Promise<IDefaultInstagramEngagersResponse> => {
  const params = $.param(req ?? {});
  const res = await instagramDefaultUserFetch(`/engagers?${params}`);
  return res.json();
};
